import { useMemo, useRef, useState, useSyncExternalStore, type PointerEvent } from "react";
import { Search } from "lucide-react";
import { getAllComponents, type CurvaDocument } from "@/document/model/document";
import type { ComponentNode } from "@/document/model/nodes";
import {
  componentThumbnailLabel,
  getComponentThumbnail,
  getComponentThumbnailSnapshot,
  refreshComponentThumbnail,
  subscribeComponentThumbnails,
} from "@/document/previews/componentThumbnails";
import { filterByFuzzySearch } from "@/lib/search/fuzzySearch";
import { ComponentContextMenu } from "./ComponentContextMenu";

const COMPONENT_DRAG_THRESHOLD = 4;

interface ComponentsPanelProps {
  document: CurvaDocument;
  onComponentPlacementStart: (componentId: string) => void;
  onLocateComponent: (componentId: string) => void;
}

export function ComponentsPanel({
  document,
  onComponentPlacementStart,
  onLocateComponent,
}: ComponentsPanelProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [contextMenu, setContextMenu] = useState<{ componentId: string; x: number; y: number } | undefined>(undefined);
  const pendingDragRef = useRef<{ componentId: string; startX: number; startY: number; placementStarted: boolean } | undefined>(undefined);
  useSyncExternalStore(subscribeComponentThumbnails, getComponentThumbnailSnapshot, getComponentThumbnailSnapshot);

  const components = useMemo(
    () => [...getAllComponents(document)].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: "base" })),
    [document],
  );
  const filteredComponents = useMemo(
    () => filterByFuzzySearch(components, searchQuery, component => componentThumbnailLabel(component.name)),
    [components, searchQuery],
  );

  function handlePointerDown(component: ComponentNode, event: PointerEvent<HTMLButtonElement>): void {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    void refreshComponentThumbnail(document, component.id);
    pendingDragRef.current = {
      componentId: component.id,
      startX: event.clientX,
      startY: event.clientY,
      placementStarted: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: PointerEvent<HTMLButtonElement>): void {
    const pending = pendingDragRef.current;
    if (!pending || pending.placementStarted) {
      return;
    }

    const distance = Math.hypot(event.clientX - pending.startX, event.clientY - pending.startY);
    if (distance < COMPONENT_DRAG_THRESHOLD) {
      return;
    }

    pending.placementStarted = true;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onComponentPlacementStart(pending.componentId);
  }

  function handlePointerUp(event: PointerEvent<HTMLButtonElement>): void {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pendingDragRef.current = undefined;
  }

  return (
    <>
      <div className="shrink-0 border-b border-white/10 px-3 py-2">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search components"
            aria-label="Search components"
            className="h-7 w-full rounded-sm border border-white/10 bg-[#0f1318] py-1 pl-7 pr-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-400/70"
          />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {components.length === 0 ? (
          <div className="px-1 py-6 text-center text-slate-500">No components yet</div>
        ) : filteredComponents.length === 0 ? (
          <div className="px-1 py-6 text-center text-slate-500">No matching components</div>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-4">
            {filteredComponents.map(component => {
              const thumbnail = getComponentThumbnail(component.id);
              return (
                <button
                  key={component.id}
                  type="button"
                  className="group flex flex-col items-center gap-2 rounded p-1 text-center outline-none transition-colors hover:bg-white/[0.04] focus-visible:ring-1 focus-visible:ring-sky-400/50"
                  onPointerDown={event => handlePointerDown(component, event)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  onContextMenu={event => {
                    event.preventDefault();
                    setContextMenu({ componentId: component.id, x: event.clientX, y: event.clientY });
                  }}
                >
                  <div className="grid size-20 place-items-center rounded-lg bg-white shadow-md shadow-black/25">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt=""
                        draggable={false}
                        className="max-h-[72px] max-w-[72px] object-contain"
                      />
                    ) : (
                      <div className="size-[72px] rounded bg-white/80" />
                    )}
                  </div>
                  <span className="line-clamp-2 w-full px-1 text-[11px] leading-tight text-slate-200">{componentThumbnailLabel(component.name)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {contextMenu ? (
        <ComponentContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onLocate={() => {
            onLocateComponent(contextMenu.componentId);
            setContextMenu(undefined);
          }}
          onClose={() => setContextMenu(undefined)}
        />
      ) : null}
    </>
  );
}
